import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, Globe } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { DIRECTORS, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_LINK } from "@/data/content";
import { useLang } from "@/i18n";

export const Footer = () => {
  const { t, lang } = useLang();
  return (
  <footer data-testid="site-footer" className="border-t border-gold/40 bg-[#030303] overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24">
      <div className="flex flex-col lg:flex-row justify-between gap-14 pb-20">
        <div className="max-w-sm">
          <p className="font-serif text-2xl mb-4">New Creation <span className="text-gold">Studio</span></p>
          <p className="text-white/50 text-sm leading-relaxed mb-8">
            {t("ft.tagline")}
          </p>
          <div className="space-y-3 text-sm">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email"
              className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors duration-300">
              <Mail size={15} className="text-gold shrink-0" /> {CONTACT_EMAIL}
            </a>
            <a href={CONTACT_PHONE_LINK} data-testid="footer-phone"
              className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors duration-300">
              <Phone size={15} className="text-gold shrink-0" /> {CONTACT_PHONE}
            </a>
          </div>
        </div>
        <div className="flex gap-20">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-5">{t("ft.nav")}</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/le-studio" data-testid="footer-link-studio" className="text-white/60 hover:text-gold transition-colors duration-300">{t("nav.studio")}</Link></li>
              <li><Link to="/prestations" data-testid="footer-link-prestations" className="text-white/60 hover:text-gold transition-colors duration-300">{t("nav.prestations")}</Link></li>
              <li><Link to="/galerie" data-testid="footer-link-galerie" className="text-white/60 hover:text-gold transition-colors duration-300">{t("nav.galerie")}</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="text-white/60 hover:text-gold transition-colors duration-300">{t("nav.contact")}</Link></li>
              <li><Link to="/admin" data-testid="footer-link-admin" className="text-white/40 hover:text-gold transition-colors duration-300">{t("ft.admin")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-5">{t("ft.team")}</p>
            <div className="space-y-6">
              {DIRECTORS.map((d) => (
                <div key={d.handle}>
                  <p className="text-xs text-white/40 mb-2">{lang === "en" ? t(d.handle === "@artwhos" ? "st.role1" : "st.role2") : d.role}</p>
                  <div className="flex flex-wrap gap-2">
                    <a href={`https://instagram.com/${d.handle.slice(1)}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`footer-instagram-${d.handle.slice(1)}`}
                      className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-white/60 transition-colors duration-300 hover:border-gold hover:text-gold">
                      <Instagram size={13} /> {d.handle}
                    </a>
                    <a href={d.siteUrl} target="_blank" rel="noopener noreferrer"
                      data-testid={`footer-site-${d.handle.slice(1)}`}
                      className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-white/60 transition-colors duration-300 hover:border-gold hover:text-gold">
                      <Globe size={13} /> {d.site}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Reveal>
        <p className="font-serif font-light uppercase leading-[0.9] tracking-tighter text-[11vw] sm:text-[9vw] lg:text-[7.5vw] text-white/[0.07] select-none text-center w-full -mb-[1.5vw]" aria-hidden="true">
          {t("ft.giant")}
        </p>
      </Reveal>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row justify-between gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/30">
        <span>© {new Date().getFullYear()} New Creation Studio</span>
        <span>{t("ft.bottom")}</span>
      </div>
    </div>
  </footer>
  );
};
